import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Award, BookOpen, Briefcase, Globe, Scale, GraduationCap } from 'lucide-react';

const FoundingPartner = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      label: 'Leadership',
      title: 'Our Founding Partner',
      name: 'R. Hakan Hamidoğulları',
      role: 'Founder & Managing Partner',
      education: 'Koç University Faculty of Law, 2018',
      bio: 'R. Hakan Hamidoğulları brings extensive experience in corporate law, contracts law, real estate law, and enforcement law from his practice at leading Turkish law firms. Following his legal career foundation, he pursued advanced studies through the prestigious WIPO-University of Turin LLM program in Intellectual Property, combining practical expertise with specialized international education.',
      quote: 'Our vision is to build an internationally recognized intellectual property practice while delivering excellence across all our legal services.',
      credentials: [
        { icon: GraduationCap, text: 'Koç University Faculty of Law, 2018' },
        { icon: Award, text: 'LLM in IP - WIPO/University of Turin' },
        { icon: Briefcase, text: 'Corporate Law, Contracts Law, Real Estate Law, Enforcement Law' },
        { icon: Globe, text: 'International Perspective & Network' },
      ]
    },
    tr: {
      label: 'Yönetim',
      title: 'Kurucu Ortağımız',
      name: 'R. Hakan Hamidoğulları',
      role: 'Kurucu Avukat',
      education: 'Koç Üniversitesi Hukuk Fakültesi, 2018',
      bio: 'R. Hakan Hamidoğulları, Türkiye\'nin önde gelen hukuk bürolarında ticaret hukuku, sözleşmeler hukuku, gayrimenkul hukuku ve icra hukuku alanlarında kapsamlı deneyim kazanmıştır. Hukuk kariyerinin ardından, Dünya Fikri Mülkiyet Örgütü (WIPO) ve Torino Üniversitesi ortaklığında sunulan Fikri Mülkiyet Hukuku Yüksek Lisans programını tamamlayarak uluslararası düzeyde uzmanlaşmıştır.',
      quote: 'Hedefimiz, fikri mülkiyet alanında uluslararası düzeyde tanınan bir hukuk bürosu olmak ve tüm faaliyet alanlarımızda yüksek kaliteli hukuki hizmet sunmaktır.',
      credentials: [
        { icon: GraduationCap, text: 'Koç Üniversitesi Hukuk Fakültesi, 2018' },
        { icon: Award, text: 'Fikri Mülkiyet Hukuku LL.M. - WIPO/Torino Üniversitesi' },
        { icon: Briefcase, text: 'Ticaret Hukuku, Sözleşmeler Hukuku, Gayrimenkul Hukuku, İcra Hukuku' },
        { icon: Globe, text: 'Uluslararası Perspektif ve İş Ağı' },
      ]
    },
    es: {
      label: 'Liderazgo',
      title: 'Nuestro Socio Fundador',
      name: 'R. Hakan Hamidoğulları',
      role: 'Fundador y Socio Director',
      education: 'Facultad de Derecho de la Universidad Koç, 2018',
      bio: 'R. Hakan Hamidoğulları aporta amplia experiencia en derecho mercantil, derecho contractual, derecho inmobiliario y derecho de ejecución de su práctica en los principales bufetes turcos. Tras su carrera jurídica, realizó estudios avanzados a través del prestigioso programa LLM en Propiedad Intelectual de la OMPI-Universidad de Turín.',
      quote: 'Nuestra visión es construir una práctica de propiedad intelectual reconocida internacionalmente mientras brindamos excelencia en todos nuestros servicios legales.',
      credentials: [
        { icon: GraduationCap, text: 'Facultad de Derecho de la Universidad Koç, 2018' },
        { icon: Award, text: 'LLM en PI - OMPI/Universidad de Turín' },
        { icon: Briefcase, text: 'Derecho Mercantil, Derecho Contractual, Derecho Inmobiliario, Derecho de Ejecución' },
        { icon: Globe, text: 'Perspectiva y Red Internacional' },
      ]
    },
    fr: {
      label: 'Direction',
      title: 'Notre Associé Fondateur',
      name: 'R. Hakan Hamidoğulları',
      role: 'Fondateur & Associé Gérant',
      education: 'Faculté de Droit de l\'Université Koç, 2018',
      bio: 'R. Hakan Hamidoğulları apporte une vaste expérience en droit des affaires, droit des contrats, droit immobilier et droit de l\'exécution de sa pratique dans les principaux cabinets turcs. Après sa carrière juridique, il a poursuivi des études avancées à travers le prestigieux programme LLM en Propriété Intellectuelle de l\'OMPI-Université de Turin.',
      quote: 'Notre vision est de construire une pratique de propriété intellectuelle reconnue internationalement tout en offrant l\'excellence dans tous nos services juridiques.',
      credentials: [
        { icon: GraduationCap, text: 'Faculté de Droit de l\'Université Koç, 2018' },
        { icon: Award, text: 'LLM en PI - OMPI/Université de Turin' },
        { icon: Briefcase, text: 'Droit des Affaires, Droit des Contrats, Droit Immobilier, Droit de l\'Exécution' },
        { icon: Globe, text: 'Perspective et Réseau International' },
      ]
    }
  };

  const t = content[language] || content.en;

  return (
    <section id="partner" className="relative bg-[#1e3a5f] py-24 lg:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image/Visual */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&auto=format&fit=crop&q=80"
                alt="Law Library"
                className="w-full h-full object-cover"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/80 via-transparent to-transparent" />
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-bold text-[#1e3a5f]">WIPO Accredited</p>
                  <p className="text-gray-500 text-sm">International IP Education</p>
                </div>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-white/20 rounded-2xl" />
          </div>

          {/* Right - Content */}
          <div className="text-white space-y-8">
            <div>
              <span className="text-blue-300 font-semibold text-sm uppercase tracking-wider">{t.label}</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
                {t.title}
              </h2>
              <h3 className="text-2xl md:text-3xl text-white font-bold mb-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
                {t.name}
              </h3>
              <p className="text-blue-200 text-lg">{t.role}</p>
            </div>

            <p className="text-blue-100 text-lg leading-relaxed">
              {t.bio}
            </p>

            {/* Quote */}
            <blockquote className="border-l-4 border-blue-400 pl-6 py-2">
              <p className="text-white italic text-lg">
                "{t.quote}"
              </p>
            </blockquote>

            {/* Credentials */}
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {t.credentials.map((cred, index) => {
                const IconComponent = cred.icon;
                return (
                  <div key={index} className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <IconComponent className="w-5 h-5 text-blue-300 flex-shrink-0" />
                    <span className="text-blue-100 text-sm">{cred.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundingPartner;
